import {
  Box,
  BoxProps,
  Button,
  Flex,
  FlexProps,
  Heading,
  Image,
  ImageProps,
  Text,
} from '@chakra-ui/react'
import React from 'react'
import CountUp from 'react-countup'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-scroll'
import bagImage from 'src/assets/images/bag.png'
import bottleImage from 'src/assets/images/bottle.png'
import bubblesLg from 'src/assets/images/bubbles-lg.svg'
import bubblesSm from 'src/assets/images/bubbles-sm.svg'
import reuseImage from 'src/assets/images/reuse.png'
import { colors } from 'src/theme/colors'

type InfoBoxProps = {
  positionStyles: Record<string, any>
  countEnd: number
  postfix: string
  textContent: string
  color?: string
}

const HomeHeader: React.FC = () => {
  const { t } = useTranslation()

  const renderInfoBox = ({
    positionStyles,
    countEnd,
    postfix,
    textContent,
    color = colors.primary,
  }: InfoBoxProps) => (
    <Box {...styles.infoBox} {...positionStyles}>
      <Text {...styles.countText} color={color}>
        <CountUp start={1} end={countEnd} />
        {postfix}+
      </Text>
      <Text {...styles.textStyles} textAlign='center' fontWeight='600'>
        {textContent}
      </Text>
    </Box>
  )

  const renderLeftContainer = () => (
    <Flex {...styles.leftContainer}>
      <Heading {...styles.headingText}>{t('homeHeader.title')}</Heading>
      <Text {...styles.textStyles}>{t('homeHeader.text1')}</Text>
      <Text {...styles.textStyles}>{t('homeHeader.text2')}</Text>
      <Text {...styles.textStyles}>{t('homeHeader.text3')}</Text>
      <Button
        as={Link}
        to='howItWorksElement'
        smooth={true}
        offset={-70}
        duration={500}
        {...styles.linkButton}
      >
        {t('homeHeader.linkText')}
      </Button>
    </Flex>
  )

  const renderRightContainer = () => (
    <Flex {...styles.rightContainer}>
      <Image src={reuseImage} {...styles.image1} />
      <Image src={bagImage} {...styles.image2} />
      <Image src={bottleImage} {...styles.image3} />
      {renderInfoBox({
        positionStyles: { left: '-10%', bottom: '12%' },
        countEnd: 10,
        postfix: 'K',
        textContent: t('homeHeader.deliveryText'),
      })}
      {renderInfoBox({
        positionStyles: { left: '40%', top: '20%' },
        countEnd: 1000,
        postfix: 'K',
        textContent: t('homeHeader.recycledItemsText'),
        color: colors.red,
      })}
      {renderInfoBox({
        positionStyles: { right: '20%', bottom: '0' },
        countEnd: 525,
        postfix: 'M',
        textContent: t('homeHeader.savedCO2Text'),
      })}
    </Flex>
  )

  const renderSubtitle = () => (
    <Text fontSize='5xl' fontWeight={'bold'}>
      <Text as='span' color='red'>
        {t('homeHeader.subtitlePart1')}
      </Text>
      <Text as='span' color={colors.primary}>
        {t('homeHeader.subtitlePart2')}
      </Text>
    </Text>
  )

  return (
    <Box as='section' {...styles.container}>
      <Flex wrap='wrap'>
        {renderLeftContainer()}
        {renderRightContainer()}
      </Flex>
      {renderSubtitle()}
    </Box>
  )
}

export default HomeHeader

const styles = {
  container: {
    padding: '5em',
  },
  leftContainer: {
    width: ['100%', '100%', '50%'],
    order: [2, 2, 1],
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    minHeight: '707px',
    position: 'relative',
    backgroundImage: `url(${bubblesLg})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'top right',
  } as FlexProps,
  headingText: {
    fontSize: '6xl',
    marginBottom: '2rem',
    fontWeight: '800',
  },
  textStyles: {
    color: 'gray.600',
    maxWidth: '80%',
    marginBottom: '1rem',
    fontSize: '3xl',
  },
  linkText: {
    marginTop: '52px',
    bg: '#1D6973',
    color: 'white',
    padding: '0.8rem 2rem',
    borderRadius: '5px',
    fontSize: '1.05rem',
    lineHeight: '1.3',
    fontWeight: '600',
  },
  rightContainer: {
    width: ['100%', '100%', '50%'],
    order: [1, 1, 2],
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    minHeight: '707px',
    paddingRight: '5%',
    backgroundImage: `url(${bubblesSm})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'top right',
  } as FlexProps,
  image1: {
    position: 'absolute',
    left: '15%',
    top: '0',
    width: '61%',
    height: '37%',
    objectFit: 'cover' as const,
    borderRadius: '30px',
    boxShadow: 'xl',
  } as ImageProps,
  image2: {
    position: 'absolute',
    left: '16px',
    bottom: '15%',
    width: '66%',
    height: '41%',
    zIndex: 1,
    objectFit: 'cover' as const,
    borderRadius: '30px',
    boxShadow: 'xl',
  } as ImageProps,
  image3: {
    position: 'absolute',
    right: '16px',
    bottom: '15%',
    width: '37%',
    height: '70%',
    objectFit: 'cover' as const,
    borderRadius: '30px',
    boxShadow: 'xl',
  } as ImageProps,
  infoBox: {
    position: 'absolute',
    borderRadius: '30px',
    padding: '28px 24px',
    backgroundColor: 'white',
    boxShadow: 'xl',
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '15em',
    width: '18em',
  } as BoxProps,
  countText: {
    fontSize: '65px',
    fontWeight: 'bold',
  },
  countContainer: {},
  countTextContainer: {
    minHeight: '100px',
    minWidth: '220px',
  },
  linkButton: {
    marginTop: '52px',
    backgroundColor: colors.primary,
    color: 'white',
    padding: '0.8rem 2rem',
    borderRadius: '5px',
    fontSize: '1.05rem',
    lineHeight: '1.3',
    fontWeight: '600',
    _hover: {
      backgroundColor: colors.primary2,
    },
  },
}
